import { createTheme } from '@mui/material';
import '@fontsource/kanit/300.css';
import '@fontsource/kanit/400.css';
import '@fontsource/kanit/600.css';
import { MuiButton, MuiTypography, MuiCheckbox, MuiAccordion, MuiAccordionSummary, MuiAccordionDetails } from './components';

// ============================================================================
// COLOR TOKENS — single source of truth for every color used across the
// Edge & Cloud FrontEnd (/src and /srcCloud). Do NOT hardcode hex/rgb(a)
// values in components or CSS files — import/reference these tokens instead:
//  - React (sx prop): theme.palette.<group>.<key> (via useTheme()), or MUI's
//    own shorthand slots (e.g. "common.white", "primary.contrastText").
//  - Plain JS/DOM (no sx, e.g. SVG string templates, style.fill=...): import
//    the named export directly from this file, e.g. `import { status } from '...theme/muiTheme'`.
//  - CSS files (*.css): use the `--os-*` custom properties emitted by ThemeWrapper.js,
//    which are generated automatically from these token groups.
// ============================================================================

// Brand colors — shared between dark and light themes
// https://coolors.co/029a4a-f69218-b10f2e-56d552-0d0e0f
export const kontram = {
  primary: '#029a4a',   // Kontram brand green
  secondary: '#56D552', // Kontram brand light green
  red: '#b10f2e',       // Brand error red
  redBright: '#e00b0b', // Canonical bright/high-visibility red — icon fills, dots, labels, out-of-range text on dark surfaces.
  redOverlay: 'rgba(177, 15, 46, 0.86)', // translucent brand red (failed-state accordion header)
  warning: '#f69218',   // Brand warning orange
};

// Semantic status colors (alarms, badges, indicators, report highlights).
// Kept as distinct shades rather than merged into `kontram` because each is
// used for a specific status/severity nuance in a particular view.
export const status = {
  success: kontram.primary,               // OK / normal state
  warning: kontram.warning,                // warning state
  error: kontram.red,                      // alarm / error state
  errorBright: kontram.redBright,          // device fault icon fill (graphical view alarm-active path)
  errorAlt: kontram.redBright,              // sample condition / device category fault text (graphical view)
  errorLight: kontram.redBright,           // single-item pie chart slice, dev-panel error text/border
  errorDot: kontram.redBright,             // bright red status dot (alarm history list)
  alarmLabel: kontram.redBright,           // "Alarm" severity label text (edit alarm ranges)
  positive: kontram.secondary,    // positive change / increase text
  positiveBg: 'rgba(46, 125, 50, 0.10)',   // positive change row background
  updatedHighlight: 'rgba(246, 146, 24, 0.15)', // "value updated/pending" row highlight (orange tint)
};

const heatmap = {
  level1: 'rgba(46, 125, 50, 0.25)',   // Dark green
  level2: 'rgba(124, 175, 76, 0.4)',   // Light green
  level3: 'rgba(255, 235, 59, 0.55)',  // Yellow
  level4: 'rgba(255, 153, 0, 0.66)',   // Orange
  level5: 'rgba(182, 34, 34, 0.8)',    // Red
};

// Neutral, mode-independent surface colors for dark panels/cards/borders that
// intentionally keep the same look regardless of the active light/dark app theme.
export const surface = {
  panel: '#272727',                         // dark card/panel background (alarms, reports, modals)
  panelDark: '#303030',                     // darker panel background (edge unit status card)
  panelTransparent: '#27272793',            // translucent dark panel overlay (graphical view valve icon)
  muted: '#4e4e4e',                         // muted grey background/border
  mutedBorder: '#555555',                   // muted grey border
  divider: '#333333',                       // secondary dark surface / divider color
  disabled: '#353535',                      // disabled control background
  neutralBorder: 'rgb(128, 128, 128)',      // neutral grey border (edge unit status card)
  overlay: '#3a3a3a',                       // raised overlay background (dev/debug panel)
  overlayAlt: '#2a2a2a',                    // secondary overlay background (dev/debug panel, inputs)
  chipActiveBg: '#1a4a6b',                  // active chip/input background (highlighted state)
  rowBorder: '#5C5D5E',                     // subtle row divider border (sensor table)
  hairline: 'rgba(255, 255, 255, 0.04)',    // near-invisible divider border
  subtleDivider: 'rgba(255, 255, 255, 0.12)', // subtle light divider on dark surfaces
  shadowDark: 'rgb(20, 20, 20)',            // drop shadow on dark cards
  dashedBorder: 'rgba(90, 90, 90, 0.5)',    // dashed row/column separators
  tooltipBg: 'rgba(93, 93, 93, 0.6)',       // chart tooltip background
  printBorder: '#cccccc',                   // light grey border used in @media print stylesheets
  printBg: '#f0f0f0',                       // light grey background used in @media print stylesheets
};

// Plain text/foreground tones used regardless of theme mode (e.g. text drawn on
// always-dark surfaces such as graphical device diagrams and dark modals).
export const staticText = {
  muted: '#c0c0c0',                    // muted grey text on dark surfaces
  faint: '#b3b3b3',                    // faint grey text (dev/debug panel labels)
  onWhite: 'rgba(0, 0, 0, 0.87)',      // standard dark text on a (always-white) surface
  borderLight: 'rgba(255, 255, 255, 0.7)', // light border/rule on dark surfaces
  coolWhite: 'rgb(235, 239, 255)',     // slightly cool-toned white text accent
};

// Top bar (DashboardTopBar) translucent overlay tones over the primary-colored app bar
export const topBar = {
  borderMuted: 'rgba(255, 255, 255, 0.3)',
  borderFaint: 'rgba(255, 255, 255, 0.25)',
  hoverBg: 'rgba(255, 255, 255, 0.15)',
  activeBg: 'rgba(0, 0, 0, 0.15)',
  textMuted: 'rgba(255, 255, 255, 0.7)',
};

// Converts a '#rrggbb' hex color to an 'rgba(r, g, b, alpha)' string — lets the gauge
// track/glow/dim variants below derive directly from the shared status colors
// (kontram.warning / kontram.redBright / kontram.primary) instead of an independent,
// hand-picked orange/red/green sub-palette.
const withAlpha = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Linear gauge component color states (normal / warning / alarm). Warning and alarm
// tracks/thumbs/ticks derive from `kontram.warning` and `kontram.redBright` (the same
// colors used for these severities everywhere else in the app) via `withAlpha`, rather
// than a separate hand-picked set of oranges/reds. Pulsing colored "glow" box-shadows
// were removed; only the plain ambient `trackShadow` remains.
export const gauge = {
  trackNormal: 'rgb(255, 255, 255)',
  trackShadow: 'rgba(255, 255, 255, 0.2)',
  warningTrack: withAlpha(kontram.warning, 0.55),
  warningTrackDim: withAlpha(kontram.warning, 0.35),
  alarmTrack: withAlpha(kontram.redBright, 0.55),
  alarmTrackDim: withAlpha(kontram.redBright, 0.35),
  thumbNormal: kontram.primary,
  thumbWarning: kontram.warning,
  thumbAlarm: kontram.redBright,
  tickOptimal: 'rgb(207, 207, 207)',
  tickWarning: kontram.warning,
  tickWarningActive: withAlpha(kontram.warning, 0.8),
  tickAlarm: kontram.redBright,
  tickAlarmActive: withAlpha(kontram.redBright, 0.8),
  fillWarningDim: withAlpha(kontram.warning, 0.3),
  fillAlarmDim: withAlpha(kontram.redBright, 0.3),
  fillThumb: kontram.secondary,
};

// CEMS/device graphical-diagram (SVG) specific colors.
export const graphicalDevice = {
  pipeDefault: '#008000',                 // default sample line color
  panelBg: surface.panel,                 // device diagram panel background
  panelBgTransparent: surface.panelTransparent, // valve icon translucent fill
  buttonInactiveBg: '#4b4544',            // disabled button background (auto/service mode off)
  buttonInactiveText: 'rgb(154, 154, 154)', // disabled button text
  outlineDark: 'rgb(0, 0, 0)',            // disabled valve icon outline / valve fill
  outlineLight: 'rgb(224, 224, 224)',     // valve icon stroke
  stateInactive: '#024421',               // inactive/off valve state fill (dark green)
  fillDarkGreen: 'rgb(2, 68, 33)',        // "blink green" animation fill
  fillPaleYellow: 'rgb(239, 253, 174)',   // "blink text" animation fill
  fillNeutral: '#333333',                 // idle icon fill ("blink" animations base state)
  textDanger: kontram.redBright,          // device/sample-condition alarm text
  alarmStroke: kontram.red,               // device card alarm border
  alarmFill: kontram.redBright,           // device alarm icon fill
};

// Dev/debug panel colors (trend DateTimeFilter data-loading tool).
export const devPanel = {
  background: '#3a3a3a',
  backgroundAlt: '#2a2a2a',
  border: '#6e6e6e',
  activeBg: '#1a4a6b',
  infoBlue: '#007acc',
  infoBlueLight: '#90caf9',
  infoBlueOverlay: 'rgba(0, 122, 204, 0.1)',
  success: '#4caf50',
  warning: '#ff9800',
  error: kontram.redBright,
  textMuted: '#b3b3b3',
};

// Map markers & sensor-list highlight colors
export const map = {
  markerWarning: kontram.warning,
  markerSuccess: kontram.primary,
  rowBorder: surface.muted,
};

export const sensorTable = {
  amber: '#f0a84f',
  amberBright: '#fca438',
  amberBorder: '#b46f085d',
  rowBorder: '#5C5D5E',
  textShadow: 'rgb(26, 16, 5)',
  mutedBg: 'rgba(160, 160, 160, 0.25)',
  transparentBorder: 'rgba(255, 255, 255, 0)',
  hoverBorder: 'rgba(255, 255, 255, 0.2)',
};

// Chart trend line series colors (previously in constants/_Colors.js).
// Redesigned to stay legible against the dark, slightly green-tinted trend
// view background (see `dashboard.backgroundGradient`).
// Structured for maximum mutual contrast: the first 9 entries are each a
// distinct, fully-saturated "vivid" hue (yellow, orange, pink, green, teal,
// purple, blue, indigo, violet) — these are used first and are the ones most
// likely to appear together on screen. The remaining 9 entries are lighter
// tints of those same hues, listed in REVERSE order so each tint sits as far
// as possible (in array position) from its vivid counterpart, minimizing the
// chance of two similar-hue lines appearing side by side even when many
// series are shown at once.
// Hue angles were re-checked (both within the vivid group and, separately,
// within the light-tint group, since tints get shown together too) so no two
// entries in either group sit closer than ~25° apart on the color wheel:
//  - Orange moved to a deeper red-orange (#FF5722) to open up more space from
//    Yellow (was only ~13° apart, now ~35°).
//  - Cyan was replaced with Teal (#009688) and Blue moved to #0288D1 — the
//    original Cyan/Blue/Indigo trio was crammed into a ~44° arc (as little as
//    ~20° apart); Teal + the adjusted Blue now spread that same region out
//    evenly (~25-30° between each of Teal/Blue/Indigo).
// Green/orange are still standard Material palette tones, distinct enough in
// saturation/lightness from the brand `kontram.primary`/`kontram.warning`
// colors to avoid being mistaken for a status indicator.
export const chartSeries = [
  '#FDD835', // Yellow (vivid)
  '#FF5722', // Orange (vivid)
  '#E91E63', // Pink (vivid)
  '#4CAF50', // Green (vivid)
  '#009688', // Teal (vivid)
  '#9C27B0', // Purple (vivid)
  '#0288D1', // Blue (vivid)
  '#3F51B5', // Indigo (vivid)
  '#673AB7', // Violet (vivid)
  '#B39DDB', // Violet (light)
  '#9FA8DA', // Indigo (light)
  '#81D4FA', // Blue (light)
  '#CE93D8', // Purple (light)
  '#80CBC4', // Teal (light)
  '#A5D6A7', // Green (light)
  '#F48FB1', // Pink (light)
  '#FFAB91', // Orange (light)
  '#FFF176', // Yellow (light)
];

const typography = {
  fontFamily: 'Kanit, Roboto, sans-serif',
  // h1: { fontWeight: 300, fontSize: '2rem' },
  // h2: { fontWeight: 300, fontSize: '1.5rem' },
  // h3: { fontWeight: 400, fontSize: '1.25rem' },
  // h4: { fontWeight: 400, fontSize: '1.1rem' },
  // h5: { fontWeight: 400, fontSize: '1rem' },
  // h6: { fontWeight: 500, fontSize: '0.875rem' },
  // body1: { fontSize: '0.875rem' },
  // body2: { fontSize: '0.8125rem' },
  // caption: { fontSize: '0.75rem' },
};

const dashboard = {
  border: kontram.secondary,
  cardBackground: '#292929',
  backgroundGradient: 'linear-gradient(145deg, #232a2a 0%, #181b19 40%, #131a17 70%, #1f2924 100%)', // main dashboard panel background
};

// Shared component overrides
const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        fontFamily: 'Kanit, Roboto, sans-serif',
      },
    }),
  },
  MuiButton,
  MuiTypography,
  MuiCheckbox,
  MuiAccordion,
  MuiAccordionSummary,
  MuiAccordionDetails,
  MuiCard: {
    defaultProps: { variant: 'outlined' },
  },
  MuiDialog: {
    defaultProps: { PaperProps: { elevation: 8 } },
  },
  MuiTextField: {
    defaultProps: { size: 'small', variant: 'outlined' },
  },
  MuiSelect: {
    defaultProps: { size: 'small' },
  },
  MuiTab: {
    styleOverrides: {
      root: { textTransform: 'none', fontWeight: 400, minWidth: 80 },
    },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': { color: kontram.primary },
        '&.Mui-checked + .MuiSwitch-track': { backgroundColor: kontram.primary },
      },
    },
  },
};

export const muiDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: kontram.primary },
    secondary: { main: kontram.secondary },
    // error: { main: kontram.red },
    // warning: { main: kontram.orange },
    background: {
      default: '#000000',
      // paper: '#3f3f3f',
      light: '#0d0e0f',
      mid: '#202122',
      dark: '#252525',
      scrollbarTrack: '#000',
    },
    kontram,
    status,
    heatmap,
    dashboard,
    surface,
    staticText,
    topBar,
    gauge,
    graphicalDevice,
    devPanel,
    map,
    sensorTable,
    chartSeries,
    dark: {
      main: '#333333',
      light: '#555555',
      dark: '#1a1a1a',
      contrastText: '#ffffff',
    },
  },
  typography,
  components: {
    ...sharedComponents,
    MuiCssBaseline: {
      styleOverrides: {
        ...sharedComponents.MuiCssBaseline.styleOverrides,
        body: {
          // backgroundColor: '#1e1e1e',
          color: '#fff',
          fontFamily: 'Kanit, Roboto, sans-serif',
        },
      },
    },
  },
});

export const muiLightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: kontram.primary },
    secondary: { main: kontram.secondary },
    // error: { main: kontram.red },
    // warning: { main: kontram.orange },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
      light: '#f5f5f5',
      mid: '#eeeeee',
      dark: '#e0e0e0',
      scrollbarTrack: '#ccc',
    },
    kontram,
    status,
    heatmap,
    dashboard,
    surface,
    staticText,
    topBar,
    gauge,
    graphicalDevice,
    devPanel,
    map,
    sensorTable,
    chartSeries,
    dark: {
      main: '#424242',
      light: '#666666',
      dark: '#1a1a1a',
      contrastText: '#ffffff',
    },
  },
  typography,
  components: sharedComponents,
});


